'use client';

import type { ShopifyImage as ShopifyImageType } from '@repo/shopify';
import { Skeleton } from '@repo/ui/components/skeleton';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ShopifyImage } from '@/components/ui/shopify-image';
import { useIdleModule } from '@/lib/use-idle-module';
import { type ProductBadge, ProductBadgeStack } from './product-badges';

// react-zoom-pan-pinch is only needed once the lightbox opens (the render below is
// gated on `lightboxOpen`), so it stays out of the PDP bundle — but the chunk is
// loaded during idle time so the first tap on an image opens the lightbox
// synchronously (see use-idle-module.ts) instead of after a chunk round-trip.
const loadImageLightbox = () => import('./image-lightbox').then((m) => m.ImageLightbox);

const MOBILE_GALLERY_SIDE_PEEK = '1.375rem';
// Match container-page's 1440px cap / 64px desktop padding, ProductMain's
// 420px (lg) / 480px (xl) info column and 48px gap, and this grid's 8px gap.
const GALLERY_IMAGE_SIZES =
	'(min-width: 1440px) 420px, (min-width: 1280px) calc(50vw - 300px), (min-width: 1024px) calc(50vw - 270px), calc(100vw - 2.75rem)';

interface ProductGalleryProps {
	images: ShopifyImageType[];
	productTitle: string;
	/** Merchandising badges rendered over the first image (top-left). */
	badges?: ProductBadge[];
}

export function ProductGallery({ images, productTitle, badges = [] }: ProductGalleryProps) {
	const [lightboxOpen, setLightboxOpen] = useState(false);
	const [lightboxIndex, setLightboxIndex] = useState(0);
	const { module: ImageLightbox, load: loadLightbox } = useIdleModule(loadImageLightbox);
	const scrollRef = useRef<HTMLElement>(null);
	const progressRef = useRef<HTMLDivElement>(null);
	const isDragging = useRef(false);
	const dragStartX = useRef(0);
	const dragScrollLeft = useRef(0);
	const hasDragged = useRef(false);
	const lastMoveX = useRef(0);
	const lastMoveTime = useRef(0);
	const velocityRef = useRef(0);

	const updateProgress = useCallback(() => {
		const el = scrollRef.current;
		const bar = progressRef.current;
		if (!el || !bar) return;

		const maxScroll = el.scrollWidth - el.clientWidth;
		const count = el.children.length || 1;
		const barWidth = Math.max(10, (1 / count) * 100);

		bar.style.width = `${barWidth}%`;
		bar.style.marginLeft =
			maxScroll > 0 ? `${(el.scrollLeft / maxScroll) * (100 - barWidth)}%` : '0%';
	}, []);

	const handleMouseDown = useCallback((e: React.MouseEvent) => {
		if (window.matchMedia('(min-width: 1024px)').matches) return;
		const el = scrollRef.current;
		if (!el) return;

		isDragging.current = true;
		hasDragged.current = false;
		dragStartX.current = e.pageX - el.offsetLeft;
		dragScrollLeft.current = el.scrollLeft;
		lastMoveX.current = e.pageX;
		lastMoveTime.current = Date.now();
		velocityRef.current = 0;
		el.style.cursor = 'grabbing';
		el.style.scrollSnapType = 'none';
		el.style.scrollBehavior = 'auto';
	}, []);

	const handleMouseMove = useCallback(
		(e: React.MouseEvent) => {
			if (!isDragging.current) return;
			e.preventDefault();

			const el = scrollRef.current;
			if (!el) return;

			const x = e.pageX - el.offsetLeft;
			const walk = x - dragStartX.current;
			if (Math.abs(walk) > 5) hasDragged.current = true;

			el.scrollLeft = dragScrollLeft.current - walk;
			updateProgress();

			const now = Date.now();
			const dt = now - lastMoveTime.current;
			if (dt > 0) {
				velocityRef.current = (lastMoveX.current - e.pageX) / dt;
			}
			lastMoveX.current = e.pageX;
			lastMoveTime.current = now;
		},
		[updateProgress],
	);

	const handleMouseUp = useCallback(() => {
		const el = scrollRef.current;
		if (!el) return;

		isDragging.current = false;
		el.style.cursor = '';

		const momentum = velocityRef.current * 300;
		if (Math.abs(momentum) > 10) {
			el.style.scrollBehavior = 'smooth';
			el.scrollLeft += momentum;
		}

		requestAnimationFrame(() => {
			setTimeout(() => {
				if (!el) return;
				el.style.scrollSnapType = '';
				el.style.scrollBehavior = '';
			}, 300);
		});
	}, []);

	const handleClick = useCallback(
		(e: React.MouseEvent, index: number) => {
			if (hasDragged.current && window.matchMedia('(max-width: 1023px)').matches) {
				e.preventDefault();
				return;
			}

			setLightboxIndex(index);
			loadLightbox();
			setLightboxOpen(true);
		},
		[loadLightbox],
	);

	const handleScroll = useCallback(() => {
		updateProgress();
	}, [updateProgress]);

	const syncMobileCarouselToIndex = useCallback(
		(index: number) => {
			const el = scrollRef.current;
			if (!el || window.matchMedia('(min-width: 1024px)').matches) return;

			const slide = el.children[index] as HTMLElement | undefined;
			if (!slide) return;

			// Scroll only the carousel track; scrollIntoView would also scroll the window/modal
			// vertically and yank the page when triggered while scrolled away from the gallery.
			el.scrollTo({
				left: slide.offsetLeft - (el.clientWidth - slide.clientWidth) / 2,
				behavior: 'smooth',
			});
			requestAnimationFrame(() => {
				requestAnimationFrame(updateProgress);
			});
		},
		[updateProgress],
	);

	useEffect(() => {
		if (images.length <= 1) return;
		const frame = window.requestAnimationFrame(updateProgress);
		return () => window.cancelAnimationFrame(frame);
	}, [images.length, updateProgress]);

	if (images.length === 0) {
		return (
			<div className="flex aspect-square items-center justify-center bg-neutral-100">
				<span className="text-neutral-400">No image available</span>
			</div>
		);
	}

	const initialProgressWidth = `${Math.max(10, (1 / images.length) * 100)}%`;

	return (
		<div className="min-w-0">
			{/* One image tree changes from a carousel to a grid in CSS. The first
			    image can load eagerly without downloading a hidden layout variant. */}
			<div className="relative -mx-4 lg:mx-0">
				<section
					ref={scrollRef}
					aria-label="Product images"
					className="scrollbar-hide flex cursor-grab gap-3 overflow-x-auto px-[1.375rem] snap-x snap-mandatory scroll-smooth overscroll-x-contain lg:grid lg:cursor-auto lg:grid-cols-2 lg:gap-2 lg:overflow-visible lg:px-0"
					style={{
						scrollPaddingInline: MOBILE_GALLERY_SIDE_PEEK,
					}}
					onScroll={handleScroll}
					onMouseDown={handleMouseDown}
					onMouseMove={handleMouseMove}
					onMouseUp={handleMouseUp}
					onMouseLeave={handleMouseUp}
				>
					{images.map((image, index) => (
						<button
							key={image.url}
							type="button"
							className="relative aspect-square w-[calc(100vw-2.75rem)] flex-shrink-0 snap-center cursor-zoom-in overflow-hidden bg-neutral-100 lg:w-auto"
							onClick={(e) => handleClick(e, index)}
							aria-label={`${index === 0 && badges.length ? `${badges.map((badge) => badge.label).join(', ')}. ` : ''}Open image ${index + 1} in full screen`}
						>
							<ShopifyImage
								src={image.url}
								alt={image.altText || `${productTitle} - ${index + 1}`}
								fill
								className="pointer-events-none object-cover"
								loading={index === 0 ? 'eager' : 'lazy'}
								fetchPriority={index === 0 ? 'high' : undefined}
								decoding={index === 0 ? 'sync' : undefined}
								disableBlur={index === 0}
								sizes={GALLERY_IMAGE_SIZES}
							/>
							{index === 0 && (
								<ProductBadgeStack
									badges={badges}
									className="pointer-events-none absolute left-3 top-3 z-10 flex flex-col items-start gap-1.5"
								/>
							)}
						</button>
					))}
				</section>

				{images.length > 1 && (
					<div className="mx-4 mt-3 h-0.5 overflow-hidden bg-neutral-200 lg:hidden">
						<div
							ref={progressRef}
							className="h-full rounded-full bg-neutral-400"
							style={{ width: initialProgressWidth, marginLeft: '0%' }}
						/>
					</div>
				)}
			</div>

			{lightboxOpen && ImageLightbox && (
				<ImageLightbox
					images={images}
					initialIndex={lightboxIndex}
					productTitle={productTitle}
					onClose={(index) => {
						setLightboxIndex(index);
						setLightboxOpen(false);
						syncMobileCarouselToIndex(index);
					}}
				/>
			)}
		</div>
	);
}

export function ProductGallerySkeleton() {
	return (
		<div className="min-w-0">
			<div className="hidden gap-1 lg:grid lg:grid-cols-2">
				{Array.from({ length: 4 }, (_, i) => `grid-skeleton-${i}`).map((key) => (
					<Skeleton key={key} className="aspect-square rounded-none" />
				))}
			</div>
			<div className="relative -mx-4 lg:hidden">
				<div className="flex gap-3 px-[1.375rem]">
					<Skeleton className="aspect-square w-[calc(100vw-2.75rem)] shrink-0 rounded-none" />
				</div>
				<Skeleton className="mx-4 mt-3 h-0.5 rounded-full" />
			</div>
		</div>
	);
}
